import React from 'react';
import { createRoot } from 'react-dom/client';
import { IconQuestionMark } from '@tabler/icons-react';

/**
 * Map of legacy Font Awesome names to Tabler icon names.
 * This ensures backward compatibility with existing backend data.
 */
const legacyNameMap = {
    faGraduationCap: 'IconSchool',
    faChartBar: 'IconChartBar',
    faFile: 'IconFile',
    faLeaf: 'IconLeaf',
    faMouse: 'IconMouse',
    faEuroSign: 'IconCurrencyEuro',
    faFutbol: 'IconBallFootball',
    faCheckCircle: 'IconCircleCheck',
    faHandHoldingUsd: 'IconHandHoldingDollar',
    faHeart: 'IconHeart',
    faBirthdayCake: 'IconCake',
    faCalendar: 'IconCalendar',
    faCalendarAlt: 'IconCalendarEvent',
    faMask: 'IconMask',
    faVolumeUp: 'IconVolume',
    faCoins: 'IconCoins',
    faBoxOpen: 'IconPackage',
    faFileAlt: 'IconFileDescription',
    faUser: 'IconUser',
    faUserFriends: 'IconUsers',
    faUsers: 'IconUsersGroup',
    faAward: 'IconAward',
    faProjectDiagram: 'IconHierarchy',
    faLightbulb: 'IconLamp',
    faRocket: 'IconRocket',
    faPiggyBank: 'IconPigMoney',
    faVoteYea: 'IconChecks',
    faTrophy: 'IconTrophy',
};

const iconCache = {};

/**
 * A standalone component that lazily loads a Tabler icon by its name.
 *
 * @param {string} name - The name of the icon (e.g., 'IconUser' or legacy 'faUser')
 * @param {number} size - Optional icon size (default: 24)
 * @param {string} className - Optional CSS classes
 */
const DynamicIcon = ({ name, ...props }) => {
    const targetName = legacyNameMap[name] || name;

    // Memoize the lazy component to prevent re-creating it on every render
    const Icon = React.useMemo(() => {
        if (!targetName) return null;

        if (!iconCache[targetName]) {
            iconCache[targetName] = React.lazy(() =>
                import(
                    /* webpackChunkName: "lazy-icon-[request]" */ `@tabler/icons-react/dist/esm/icons/${targetName}.mjs`
                ).catch((err) => {
                    console.warn(`Icon "${targetName}" not found at path. Using fallback.`, err);
                    return { default: IconQuestionMark };
                })
            );
        }
        return iconCache[targetName];
    }, [targetName]);

    if (!Icon) return null;

    return (
        <React.Suspense fallback={<span style={{ width: props.size || 24, height: props.size || 24 }} />}>
            <Icon {...props} />
        </React.Suspense>
    );
};

/**
 * Register a Custom Element (Web Component) wrapper for DynamicIcon.
 * This allows using <tabler-icon name="IconUser" size="32" class="text-primary"></tabler-icon>
 * inside dangerouslySetInnerHTML or any static HTML strings.
 */
if (typeof window !== 'undefined' && !customElements.get('tabler-icon')) {
    class DynamicIconElement extends HTMLElement {
        constructor() {
            super();
            this.root = null;
        }

        connectedCallback() {
            if (!this.root) {
                this.root = createRoot(this);
            }
            this.render();
        }

        attributeChangedCallback() {
            this.render();
        }

        static get observedAttributes() {
            return ['name', 'size', 'class', 'className', 'color'];
        }

        render() {
            if (!this.root) return;

            const name = this.getAttribute('name');
            const size = this.getAttribute('size') || undefined;
            const className = this.getAttribute('class') || this.getAttribute('className') || undefined;
            const color = this.getAttribute('color') || undefined;

            this.root.render(
                <DynamicIcon
                    name={name}
                    size={size ? parseInt(size, 10) : undefined}
                    className={className}
                    color={color}
                />
            );
        }

        disconnectedCallback() {
            if (this.root) {
                // Delaying unmount to avoid race conditions during fast DOM updates
                const currentRoot = this.root;
                setTimeout(() => currentRoot.unmount(), 0);
                this.root = null;
            }
        }
    }

    customElements.define('tabler-icon', DynamicIconElement);
}

export default DynamicIcon;
